// Based on https://getbootstrap.com/docs/5.1/components/modal/

<template>
    <div id="confirmModal" class="modal fade" tabindex="-1" aria-labelledby="confirmModalLabel" aria-hidden="true">
        <div :class="`modal-dialog modal-dialog-scrollable modal-dialog-centered ${largeModal ? 'modal-lg' : ''}`">
            <div class="modal-content">
                <div v-if="title" class="modal-header">
                    <h5 id="confirmModalLabel" class="modal-title">{{ title }}</h5>
                    <button-button type="button" class="btn-close" aria-label="Close" @click="cancelClicked"></button-button>
                </div>
                <div class="modal-body">
                    {{ message }}
                </div>
                <div class="modal-footer">
                    <button-button v-if="cancelButton" type="button" class="btn btn-secondary" @click="cancelClicked">{{ cancelButton }}</button-button>
                    <button-button v-if="alt1Button" type="button" class="btn btn-secondary" @click="alt1Clicked">{{ alt1Button }}</button-button>
                    <button-button v-if="okButton" type="button" class="btn btn-primary" @click="okClicked">{{ okButton }}</button-button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    #confirmModal {
        z-index: 2000; // place above real modal
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";
import { Modal } from "bootstrap";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";

export interface AlertDialogOptions {
    okButton?: string;
    alt1Button?: string;
    cancelButton?: string;
    largeModal?: boolean;
}

let cachedAlertDialog: any = null;

export default defineComponent({
    name: "AlertDialog",
    data() {
        return {
            title: null as string | null,
            message: null as unknown as string,
            okButton: null as string | null,
            alt1Button: null as string | null,
            cancelButton: null as string | null,
            largeModal: false,
            modal: null as Modal | null,
            resolvePromise: null as unknown as (value: ButtonClicked | PromiseLike<ButtonClicked>) => void,
            rejectPromise: null as unknown as (reason?: any) => void
        };
    },
    methods: {
        internalShow(message: string, title?: string, options?: AlertDialogOptions): Promise<ButtonClicked> {
            this.killAlertDialog();
            this.title = title ?? null;
            this.message = message || "";
            this.okButton = options?.okButton || null;
            this.alt1Button = options?.alt1Button || null;
            this.cancelButton = options?.cancelButton || null;
            this.largeModal = options?.largeModal || false;
            this.modal = new Modal(document.getElementById("confirmModal")!, {
                backdrop: "static",
                keyboard: true,
                focus: true,
            });
            this.modal.show();

            // to support multiple modals we need to adjust the backdrop zindex
            const backdrops = document.querySelectorAll(".modal-backdrop.show");
            if (backdrops.length > 1) {
                const backdrop = backdrops[backdrops.length - 1] as HTMLElement;
                backdrop.style.zIndex = "1999";
            }

            return new Promise<ButtonClicked>((resolve, reject): void => {
                this.resolvePromise = resolve;
                this.rejectPromise = reject;
            });
        },
        okClicked(): void {
            if (!this.modal) return;
            this.modal.hide();
            this.resolvePromise(ButtonClicked.Ok);
        },
        cancelClicked(): void {
            if (!this.modal) return;
            this.modal.hide();
            this.resolvePromise(ButtonClicked.Cancel);
        },
        killAlertDialog(): void {
            if (!this.modal) return;
            this.modal.hide();
            this.modal = null;
        },
        alt1Clicked(): void {
            if (!this.modal) return;
            this.modal.hide();
            this.resolvePromise(ButtonClicked.Alt1);
        }
    }
});

export function initializeAlertDialog(dialog: any): void {
    cachedAlertDialog = dialog;
}

export function killAlertDialogs(): void {
    cachedAlertDialog.killAlertDialog();
}

export function showAlertDialog(message: string, title?: string, options?: AlertDialogOptions): Promise<ButtonClicked> {
    return cachedAlertDialog.internalShow(message, title, options);
}

export function confirmYesNo(message: string, title?: string): Promise<ButtonClicked> {
    return showAlertDialog(message, title, { okButton: "Yes", cancelButton: "No" });
}

export function confirmOk(message: string, title?: string): Promise<ButtonClicked> {
    return showAlertDialog(message, title, { okButton: "OK" });
}

export function confirmYesWaitNo(message: string, title?: string): Promise<ButtonClicked> {
    return showAlertDialog(message, title, { okButton: "Yes", alt1Button: "Wait", cancelButton: "No" });
}

</script>
